import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@mui/material'
import Icon from '@app/stories/Icons'
import Typography from '@app/stories/Typography'
import GeneralModal from '@app/containers/GeneralModal'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material'

import { formatDateForUI, formatCurrency } from '@app/libs/formatters'
import usePDFs from '@app/hooks/usePDFs'
import generatePDF from '@app/libs/pdf'

import { retrieveFiretableImageURL } from '@app/libs/formatters'
import useNetworks from '@app/hooks/useNetworks'
import misheText from '../assets/images/MisheText.png' // react-pdf/renderer does not support SVGs

/**
 * Creates a custom close button with a download PDF option.
 *
 * @param {Function} handleDownload - Function to handle the download of the PDF.
 * @returns {Function} - A function that renders the custom close button.
 */
const customCloseButton = (handleDownload) => {
  const DownloadButton = (
    <Button
      variant='contained'
      size='small'
      style={{ marginLeft: '8px', textTransform: 'none' }}
      onClick={handleDownload}
    >
      Download PDF
    </Button>
  )

  return (handleClose) => (
    <Box display='flex' justifyContent='space-between' mt={2}>
      {DownloadButton}
      <Button
        variant='outlined'
        size='small'
        onClick={handleClose}
        style={{ textTransform: 'none' }}
      >
        Close
      </Button>
    </Box>
  )
  // Return the custom close button
}

/**
 * Converts a voucher object into a new structure for processing.
 *
 * @param {Object} voucher - The voucher object to be converted.
 * @param {Object} voucher.customer - Customer information.
 * @param {Object} voucher.service - Service information.
 * @param {Object} voucher.organization - Organization information.
 * @returns {Object | null} - The converted voucher object or null if input is invalid.
 */
const convertVoucherIntoNewStructure = (voucher) => {
  if (!voucher) {
    return null
  }

  const { customer, service, organization } = voucher
  console.log('convertVoucherIntoNewStructure', voucher)

  return {
    ...voucher,
    point_of_service_description: service?.rendering,
    document_num: voucher.code || voucher.id,
    patient_info: {
      ...customer,
    },
    service_info: {
      ...service,
      cptCodes: service?.cptCodes || '',
      date_of_service_initial: formatDateForUI(voucher?.appointmentDate) || '',
    },
    provider_info: {
      ...organization,
    },
    financials: {
      original_charge: service?.price || 0,
      allowed_amount: service?.price || 0,
      ineligible_amount: 0,
      amount_paid: service?.price || 0,
      deductible: 0,
      coinsurance: 0,
      copay: 0,
      paid_date: formatDateForUI(voucher?.redeemedAt) || '',
    },
  }
}

/**
 * Converts an image to black using a canvas.
 *
 * @param {string} imageUrl - The URL of the image to convert.
 * @returns {Promise<string>} - A promise that resolves to the black image data URL.
 */
const convertImageToBlack = async (imageUrl) => {
  const img = new Image()
  img.crossOrigin = 'Anonymous'
  img.src = imageUrl

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height

      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        const grayscale = 0 // Set all color channels to 0 for black
        data[i] = grayscale // Red
        data[i + 1] = grayscale // Green
        data[i + 2] = grayscale // Blue
      }

      ctx.putImageData(imageData, 0, 0)
      resolve(canvas.toDataURL())
    }

    img.onerror = (err) => reject(err)
  })
}

/**
 * Renders the Explanation of Benefits (EOB) button and modal.
 *
 * @param {Object} props - Component props.
 * @param {Object[]} props.claims - Array of claim objects.
 * @param {string} [props.label] - Label for the button.
 * @param {Object} [props.bundledClaim] - Optional bundled claim object.
 * @returns {JSX.Element} - The rendered EOB button and modal.
 */
const EobButton = ({ claims = [], bundledClaim, label }) => {
  const { createEOBTemplate } = usePDFs()
  const { getNetworkByID } = useNetworks()
  const [planLogo, setPlanLogo] = useState(null)

  // Process the claims data - always do this regardless of empty claims
  const convertedVouchers = claims
    .map((claim) => {
      if (!claim?.external) {
        return convertVoucherIntoNewStructure(claim)
      }
      return claim
    })
    .filter(Boolean) // Filter out any nulls

  const totals = {
    original_charge: 0,
    allowed_amount: 0,
    ineligible_amount: 0,
    amount_paid: 0,
    deductible: 0,
    coinsurance: 0,
    copay: 0,
    paid_date: '',
    patient_paid: 0,
  }

  if (convertedVouchers.length > 0) {
    convertedVouchers.forEach((claim) => {
      const { financials } = claim
      if (financials) {
        totals.original_charge += financials.original_charge || 0
        totals.allowed_amount += financials.allowed_amount || 0
        totals.ineligible_amount += financials.ineligible_amount || 0
        totals.amount_paid += financials.amount_paid || 0
        totals.deductible += financials.deductible || 0
        totals.coinsurance += financials.coinsurance || 0
        totals.copay += financials.copay || 0
        totals.paid_date = financials?.paid_date || ''
        // total patient paid is deductible + coinsurance + copay
        totals.patient_paid += financials.deductible + financials.coinsurance + financials.copay
      }
    })
  }

  // This is so we can use the top level bundle instead of the subVouchers for any identifying information
  const convertedBundledClaim = bundledClaim ? convertVoucherIntoNewStructure(bundledClaim) : null

  const mainClaim = convertedBundledClaim || convertedVouchers[0]
  const mainPatientInfo = mainClaim?.patient_info
  const mainProviderInfo = mainClaim?.provider_info
  const mainOrganizationInfo = mainClaim?.organization

  const document_num = mainClaim?.document_num || mainClaim?.id || ''

  const getPlanLogo = async (networkID) => {
    try {
      const network = await getNetworkByID(networkID)
      if (network?.logoSource) {
        return await retrieveFiretableImageURL(network.logoSource)
      }
      return null
    } catch (error) {
      console.error('Error fetching plan logo:', error)
      return null
    }
  }

  // Always define useEffect, regardless of claims
  useEffect(() => {
    const fetchPlanLogo = async () => {
      // Only attempt to fetch if we have network data
      if (convertedVouchers.length > 0 && convertedVouchers[0]?.network?.id) {
        const logo = await getPlanLogo(convertedVouchers[0].network.id)
        if (logo) {
          try {
            const blackLogo = await convertImageToBlack(logo)
            setPlanLogo(blackLogo)
          } catch (err) {
            console.error('Error converting logo:', err)
            setPlanLogo(misheText)
          }
        } else {
          setPlanLogo(misheText)
        }
      } else {
        // Set default logo if no network ID available
        setPlanLogo(misheText)
      }
    }
    fetchPlanLogo()
  }, [convertedVouchers, getNetworkByID]) // Include all dependencies

  const handleDownloadPDF = () => {
    const template = createEOBTemplate({
      totals,
      patientInfo: mainPatientInfo,
      mainProviderInfo,
      mainOrganizationInfo,
      planInfo: { planLogo },
      mainClaimInfo: mainClaim,
      claims: convertedVouchers,
    })
    generatePDF(template, 'EOB')
  }

  // Early return AFTER all hooks are defined
  if (!claims || claims.length === 0) {
    return (
      <>
        <Typography variant='body2' style={{ marginLeft: '8px' }}>
          No Claims
        </Typography>
      </>
    )
  }

  return (
    <GeneralModal
      id='explanationOfBenefits'
      headerText='Explanation of Benefits'
      maxWidth={'lg'}
      customButton={
        <Box display='flex' alignItems='center'>
          <Button
            variant='outlined'
            size='small'
            style={{ textTransform: 'none', display: 'flex', alignItems: 'center' }}
          >
            <Icon name='assignmentOutlined' />
            <Typography variant='body2' style={{ marginLeft: '8px' }}>
              {label}
            </Typography>
          </Button>
        </Box>
      }
      customClose={customCloseButton(handleDownloadPDF)}
    >
      <Box display='flex' justifyContent='center' alignItems='center'>
        <Box p={2}>
          {mainPatientInfo && (
            <Box display='flex' justifyContent='space-between' alignItems='flex-start' pb={2}>
              <Box>
                <Typography variant='h6' fontWeight='bold' style={{ textTransform: 'capitalize' }}>
                  <b>Patient's Name: </b>
                  {`${mainPatientInfo.firstname || ''} ${mainPatientInfo.lastname || ''}`.trim() ||
                    '---'}
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Provider Name: </b>
                  {mainProviderInfo?.name || '---'}
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Practice Name: </b>
                  {mainClaim?.organization?.name || '---'}
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Plan Paid: </b>
                  <Typography variant='h6' component='span' color='error' fontWeight='bold'>
                    {formatCurrency(totals?.amount_paid || 0)}
                  </Typography>
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Patient Paid: </b>
                  <Typography variant='h6' component='span' color='error' fontWeight='bold'>
                    {formatCurrency(totals?.patient_paid || 0)}
                  </Typography>
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>It is not your responsibility to pay: </b>
                  <Typography variant='h6' component='span' color='error' fontWeight='bold'>
                    {formatCurrency(totals?.original_charge - totals?.allowed_amount || 0)}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Claim Number: </b>
                  {document_num || '---'}
                </Typography>
                <Typography variant='h6' fontWeight='bold'>
                  <b>Claim Processed Date: </b>
                  {totals?.paid_date || formatDateForUI(mainClaim?.createdAt) || '---'}
                </Typography>
                {mainClaim?.point_of_service_description && (
                  <Typography variant='h6' fontWeight='bold'>
                    <b>Place of Service: </b>
                    {mainClaim?.point_of_service_description || '---'}
                  </Typography>
                )}

                {planLogo && (
                  <Box
                    component='img'
                    src={planLogo}
                    alt='Plan Logo'
                    sx={{
                      width: '100px',
                      height: 'auto',
                      marginTop: '10px',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>DATE OF SERVICE</TableCell>
                  <TableCell>TREATMENT</TableCell>
                  <TableCell>CPT</TableCell>
                  <TableCell>BILLED</TableCell>
                  <TableCell>SAVINGS</TableCell>
                  <TableCell>CONTRACTED</TableCell>
                  <TableCell>INELIGIBLE</TableCell>
                  <TableCell>PLAN PAID</TableCell>
                  <TableCell>DEDUC</TableCell>
                  <TableCell>COINS</TableCell>
                  <TableCell>COPAY</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {convertedVouchers.map((claim, index) => {
                  const { financials, service_info } = claim
                  if (!financials) return null

                  return (
                    <TableRow key={index}>
                      <TableCell>{service_info?.date_of_service_initial || '---'}</TableCell>
                      <TableCell>{service_info?.name || 0}</TableCell>
                      <TableCell>
                        {Array.isArray(service_info?.cptCodes) ? (
                          service_info.cptCodes.map((code, index) => (
                            <Typography key={index} variant='body2'>
                              {code}
                              {index < service_info.cptCodes.length - 1 && ', '}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant='body2'>{service_info?.cptCodes || '---'}</Typography>
                        )}
                      </TableCell>
                      <TableCell>---</TableCell>
                      <TableCell>
                        {formatCurrency(financials?.original_charge - financials?.allowed_amount)}
                      </TableCell>
                      <TableCell>{formatCurrency(financials?.allowed_amount || 0)}</TableCell>
                      <TableCell>{formatCurrency(financials?.ineligible_amount || 0)}</TableCell>
                      <TableCell>{formatCurrency(financials?.amount_paid || 0)}</TableCell>
                      <TableCell>{formatCurrency(financials?.deductible || 0)}</TableCell>
                      <TableCell>{formatCurrency(financials?.coinsurance || 0)}</TableCell>
                      <TableCell>{formatCurrency(financials?.copay || 0)}</TableCell>
                    </TableRow>
                  )
                })}
                <TableRow>
                  <TableCell colSpan={3}>CLAIM TOTALS</TableCell>
                  <TableCell>---</TableCell>
                  <TableCell>
                    {formatCurrency(totals.original_charge - totals.allowed_amount)}
                  </TableCell>
                  <TableCell>{formatCurrency(totals.allowed_amount)}</TableCell>
                  <TableCell>{formatCurrency(totals.ineligible_amount)}</TableCell>
                  <TableCell>{formatCurrency(totals.amount_paid)}</TableCell>
                  <TableCell>{formatCurrency(totals.deductible)}</TableCell>
                  <TableCell>{formatCurrency(totals.coinsurance)}</TableCell>
                  <TableCell>{formatCurrency(totals.copay)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </GeneralModal>
  )
}

EobButton.propTypes = {
  claims: PropTypes.arrayOf(PropTypes.object).isRequired,
  label: PropTypes.string,
}

export default EobButton
